/**
 * VoiceDictation.tsx — dictation for the whole CMS. Speak to type into the field you were last using; start a sentence
 * with the command word to run an action instead. Recordings are sent for transcription and never stored.
 *
 * How it looks and behaves follows the dictation of ChatGPT, Gemini, Google Docs and Apple:
 *  - nothing floats on the page while idle; the microphone is one quiet button in the top bar (and a keyboard shortcut);
 *  - while you speak, a small bar appears with a live waveform, a timer, Cancel and Done, and says which field it types into;
 *  - it finishes by itself when you pause (about two seconds); Done is only there to finish sooner;
 *  - after Done it shows "Writing it out…" until the text lands in your field.
 * The bar is rendered in a portal so it never moves page content.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, Mic, X } from "lucide-react";
import { DEFAULT_COMMAND_WORD, parseVoiceInput, type VoiceCommandId } from "@demandhub-cms/shared";
import { useAuth } from "@/context/AuthContext";
import { voiceApi, errorText } from "@/lib/api";
import { recordingToWav } from "@/lib/wav";
import { currentDictationTarget, describeTarget, insertDictation, trackDictationTarget, undoInTarget, type DictationTarget } from "@/lib/dictationTarget";
import { dispatchVoiceCommand } from "@/lib/voiceEvents";
import { useVoiceRecorder, type RecorderProblem } from "@/hooks/useVoiceRecorder";
import { Waveform } from "@/components/Waveform";
import { cn } from "@/lib/cn";

const PROBLEMS: Record<RecorderProblem, string> = {
  blocked: "The microphone is blocked. Click the lock icon in the address bar, allow the microphone, then try again.",
  no_microphone: "No microphone was found on this computer.",
  failed: "The recording failed. Try again.",
  silent: "I didn't hear anything. Click Dictate and start talking.",
};
const NOT_READY = "Voice typing is not switched on for this server yet. Deploy the CMS again (pnpm run deploy) and it will work. Writing and ads are not affected.";
const TOO_SHORT_SECONDS = 0.4;
const NOTICE_MS = 5000;

const ROUTES: Partial<Record<VoiceCommandId, { path: string; capability?: string }>> = {
  new_post: { path: "/content/new" },
  go_content: { path: "/content" },
  go_calendar: { path: "/calendar" },
  go_review: { path: "/review", capability: "content.publish" },
  go_ads: { path: "/ads", capability: "ai.use" },
};

type Phase = "idle" | "processing";
interface Notice {
  text: string;
  tone: "ok" | "error";
}

const clock = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function VoiceDictation({ commandWord = DEFAULT_COMMAND_WORD }: { commandWord?: string }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const reduceMotion = useReducedMotion();
  const [phase, setPhase] = useState<Phase>("idle");
  const [notice, setNotice] = useState<Notice | null>(null);
  const [intoLabel, setIntoLabel] = useState<string | null>(null);
  const noticeTimer = useRef<number>();
  // Where the words go is decided when recording starts, so clicking elsewhere mid-sentence cannot misplace them.
  const startTarget = useRef<DictationTarget | null>(null);

  const status = useQuery({ queryKey: ["voice-status"], queryFn: voiceApi.status, staleTime: 5 * 60_000, retry: 1 });
  const ready = status.data?.ready !== false;

  const say = useCallback((text: string, tone: Notice["tone"] = "ok") => {
    window.clearTimeout(noticeTimer.current);
    setNotice({ text, tone });
    noticeTimer.current = window.setTimeout(() => setNotice(null), NOTICE_MS);
  }, []);

  useEffect(() => trackDictationTarget(), []);
  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const runCommand = useCallback(
    (id: VoiceCommandId, label: string) => {
      const route = ROUTES[id];
      if (route) {
        if (route.capability && !can(route.capability as Parameters<typeof can>[0])) return say("You don't have access to that.", "error");
        navigate(route.path);
        return say(label);
      }
      if (id === "undo") return undoInTarget(currentDictationTarget()) ? say(label) : say("Nothing to undo.", "error");
      if (dispatchVoiceCommand(id)) say(label);
      else say(`"${label}" doesn't apply on this screen.`, "error");
    },
    [can, navigate, say]
  );

  const onRecorded = useCallback(
    async (audio: Blob, seconds: number) => {
      if (seconds < TOO_SHORT_SECONDS) return say("That was too short. Click Dictate and say a little more.", "error");
      setPhase("processing");
      try {
        const { text } = await voiceApi.transcribe(await recordingToWav(audio));
        const heard = parseVoiceInput(text, commandWord);
        if (heard.kind === "command") return runCommand(heard.id, heard.label);
        if (heard.kind === "unknown_command") return say(`Didn't recognise "${heard.heard}". Nothing was typed.`, "error");
        if (!heard.text) return say("Didn't catch that. Speak a little closer to the microphone and try again.", "error");
        const target = startTarget.current && startTarget.current.el.isConnected ? startTarget.current : currentDictationTarget();
        if (!target) {
          await navigator.clipboard?.writeText(heard.text).catch(() => undefined);
          return say("No field was selected, so the text was copied. Click where you want it and paste.", "error");
        }
        const result = insertDictation(target, heard.text);
        if (result === "failed") say("Couldn't type into that field.", "error");
        else if (result === "truncated") say("The field is full. Part of it was left out.", "error");
      } catch (err) {
        say(errorText(err, "Couldn't turn that into text. Try again."), "error");
      } finally {
        setPhase("idle");
      }
    },
    [commandWord, runCommand, say]
  );

  const recorder = useVoiceRecorder({ onRecorded, onProblem: (p) => say(PROBLEMS[p], "error") });
  const { supported, recording, start, stop, cancel } = recorder;

  const begin = useCallback(async () => {
    if (!ready) return say(NOT_READY, "error");
    const target = currentDictationTarget();
    startTarget.current = target;
    setIntoLabel(target ? describeTarget(target.el) : null);
    setNotice(null);
    await start();
  }, [ready, say, start]);

  const toggle = useCallback(() => {
    if (phase === "processing") return;
    if (recording) stop();
    else void begin();
  }, [phase, recording, stop, begin]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.code === "Space") {
        e.preventDefault();
        toggle();
      } else if (e.key === "Escape" && recording) cancel();
      else if (e.key === "Enter" && recording && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        stop();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [toggle, recording, cancel, stop]);

  if (!supported) return null;

  const busy = recording || phase === "processing";
  const rise = reduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 };

  return (
    <>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()} // keep the cursor in the field
        onClick={toggle}
        disabled={phase === "processing"}
        aria-pressed={recording}
        aria-label="Dictate. Speak and the words are typed for you."
        title="Dictate (Ctrl or Cmd + Shift + Space)"
        className={cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ring-1 ring-inset transition-colors disabled:opacity-60",
          recording ? "bg-danger-50 text-danger-700 ring-danger-200" : "bg-white text-neutral-700 ring-neutral-200 hover:bg-neutral-50",
          !ready && "text-neutral-400"
        )}
      >
        <Mic className="size-4" aria-hidden />
        <span className="hidden sm:inline">{recording ? "Listening…" : "Dictate"}</span>
      </button>

      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex flex-col items-center gap-3 px-4 lg:left-64" aria-live="polite">
          <AnimatePresence>
            {notice && !busy && (
              <motion.p
                key={notice.text}
                initial={rise}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                role="status"
                className={cn(
                  "pointer-events-auto max-w-md rounded-xl px-4 py-2.5 text-sm shadow-lg ring-1",
                  notice.tone === "error" ? "bg-white text-danger-700 ring-danger-200" : "bg-neutral-900 text-white ring-neutral-900"
                )}
              >
                {notice.text}
              </motion.p>
            )}
            {busy && (
              <motion.div
                key="dock"
                initial={rise}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                role="group"
                aria-label="Dictation"
                className="pointer-events-auto w-full max-w-md rounded-xl bg-neutral-900 p-3 text-white shadow-xl"
              >
                {recording ? (
                  <>
                    <p className="mb-2 px-1 text-xs text-neutral-300">
                      {intoLabel ? (
                        <>
                          Typing into <span className="font-semibold text-white">{intoLabel}</span>
                        </>
                      ) : (
                        <>No field selected. Click one first, or say “{commandWord}” and a command.</>
                      )}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancel}
                        aria-label="Cancel and discard"
                        title="Cancel (Esc)"
                        className="flex size-10 shrink-0 items-center justify-center rounded-full text-neutral-300 hover:bg-white/10 hover:text-white"
                      >
                        <X className="size-5" aria-hidden />
                      </button>
                      <div className="flex min-w-0 flex-1 items-center justify-center">
                        <Waveform analyser={recorder.analyser} />
                      </div>
                      <span className="w-11 shrink-0 text-right text-sm tabular-nums text-neutral-300">{clock(recorder.seconds)}</span>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={stop}
                        aria-label="Done. Type what I said."
                        title="Done (Ctrl or Cmd + Enter)"
                        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent-500 text-white hover:bg-accent-400"
                      >
                        <Check className="size-5" aria-hidden />
                      </button>
                    </div>
                    <p className="mt-2 px-1 text-xs text-neutral-400">Just talk. It types when you pause. Tap ✓ to finish sooner.</p>
                  </>
                ) : (
                  <div className="flex items-center gap-3 px-1 py-1.5" role="status">
                    <span className="flex gap-1" aria-hidden>
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="size-2 rounded-full bg-accent-300 motion-safe:animate-pulse" style={{ animationDelay: `${i * 160}ms` }} />
                      ))}
                    </span>
                    <span className="text-sm">Writing it out…</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </>
  );
}
